import SvgIcon from "@/components/common/SvgIcon";
import { Typography } from "@/components/kit";
import classNames from "classnames";
import React, { ReactNode, useEffect, useMemo, useState } from "react";

type TProps = {
  activeTab: string;
  setActiveTab: (value: any, type: string | undefined) => void;
  headerProps?: {
    className?: string;
    wrapperClassName?: string;
  };
  typographyClasses?: string;
  activeTypographyClasses?: string;
  selectedBorderClassName?: string;
  list: {
    title: ReactNode;
    disable?: boolean;
    isAccessible?: boolean | null;
    icon?: string;
    value: string;
    className?: string;
    type?: string;
  }[];
};

function UnderlineTabs({
  activeTab,
  list,
  setActiveTab,
  selectedBorderClassName,
  typographyClasses = "",
  activeTypographyClasses = "",
  headerProps,
}: Readonly<TProps>) {
  const elementRefs = useMemo(
    () => list.map(() => React.createRef<HTMLDivElement>()),
    [list],
  );
  const [widths, setWidths] = useState<number[]>([]);

  useEffect(() => {
    setTimeout(() => {
      const newWidths: number[] = [];
      list?.forEach((_, index) => {
        const elementRef = elementRefs[index];
        if (elementRef?.current) {
          newWidths?.push(elementRef.current.offsetWidth);
        } else {
          newWidths?.push(0);
        }
      });
      setWidths(newWidths);
    }, 100);
  }, [list]);

  const sumUntilIndex = (index: number): number => {
    // Use reduce to sum elements until the given index
    const sum = widths.reduce((acc, cur, i) => {
      if (i < index) {
        return acc + cur;
      }
      return acc;
    }, 0);

    return sum;
  };

  return (
    <div className={classNames("mb-3 px-5", headerProps?.className)}>
      <div
        className={classNames(
          "flex relative w-fit flex-row rounded-none bg-transparent mt-2 z-10",
          headerProps?.wrapperClassName,
        )}
      >
        {list?.map(
          (
            {
              title,
              value,
              disable,
              icon,
              className,
              isAccessible = true,
              type,
            },
            index,
          ) => {
            if (!isAccessible) return;
            return (
              <div
                ref={elementRefs[index]}
                key={value}
                onClick={() => {
                  if (disable) return;
                  setActiveTab(value, type);
                }}
                className={classNames(
                  "flex items-center justify-center text-center h-full relative bg-transparent antialiased  select-none cursor-pointer !opacity-100 w-fit px-4 py-3",
                  className,
                  { "!cursor-not-allowed": disable },
                )}
              >
                <div className={classNames("flex items-center gap-2")}>
                  {icon && (
                    <SvgIcon
                      fill={disable ? "fill-gray-900" : "fill-gray-300"}
                      className="w-5 h-5"
                      iconName={icon}
                    />
                  )}
                  <Typography
                    variant={
                      activeTab === value ? "Body2-Bold" : "Body2-Medium"
                    }
                    color={
                      activeTab === value
                        ? "text-gray-800"
                        : !disable
                          ? "text-gray-600"
                          : "text-gray-300"
                    }
                    className={classNames(typographyClasses, {
                      [activeTypographyClasses]: activeTab === value,
                    })}
                  >
                    {title}
                  </Typography>
                </div>
              </div>
            );
          },
        )}
        <span
          className={classNames(
            "absolute bottom-0 left-0 inline-block h-1 bg-gray-900 transform -translate-y-px transition-transform duration-300",
            selectedBorderClassName,
          )}
          style={{
            transform: `translateX(${sumUntilIndex(
              list.findIndex((obj) => obj.value === activeTab),
            )}px)`,
            width: `${
              widths[list.findIndex((obj) => obj.value === activeTab)]
            }px`,
          }}
        ></span>
      </div>
    </div>
  );
}

export default UnderlineTabs;
